import { useState, useEffect } from 'react';
import { Button, Table, Popconfirm, message, Pagination, Modal, Input, Radio, InputNumber } from 'antd';
import { FolderAddOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import Https from '@/https';
import './Role.css';

export default function RoleList() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [messageApi, messageContextHolder] = message.useMessage();
  const { TextArea } = Input;
  const [roleList, setRoleList] = useState([]);
  const [loading, setLoading] = useState(false);
  const roleColumns = [
    {
      title: '名称',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: '排序',
      dataIndex: 'sort',
      key: 'sort',
    },
    {
      title: '是否有效',
      dataIndex: 'isActive',
      key: 'isActive',
      width: 120,
      render: (isActive: string, record: any) => {
        return <span>{isActive ? '是' : '否'}</span>
      }
    },
    {
      title: '备注',
      dataIndex: 'remark',
      key: 'remark',
    },
    {
      title: '操作',
      key: 'action',
      dataIndex: 'action',
      width: 210,
      fixed: 'right' as const,
      render: (text: any, record: any) => {
        return (
          <>
          {
            record.isSystem ? null :
            <>
              <Button color="cyan" variant="link" size="small" onClick={() => handleRoleEdit(record)}>编辑</Button>
              <Button color="purple" variant="link" size="small" onClick={() => handleSetPermission(record)}>平台权限</Button>
              <Popconfirm
                title="提示"
                description="确定删除角色?"
                onConfirm={() => confirmRemove(record)}
                okText="确定"
                cancelText="取消">
                  <Button color="danger" variant="link" size="small">删除</Button>
              </Popconfirm>
            </>
          }
          </>
        )
      }
    }
  ]

  const confirmRemove = (record: any) => {
    Https.post({
      url: 'Api/URPM/V1/Role/Delete',
      data: {
        id: record.id,
      }
    }).then(res => {
      if(res.state === 1) {
        messageApi.success('删除成功');
        getRoleList();
      } else {
        messageApi.error(res.data.errorMessage || res.errorMessage);
      }
    })
  }

  const [pageIndex, setPageIndex] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);

  const getRoleList = () => {
    setLoading(true);
    Https.post({
      url: 'Api/URPM/V1/Role/List/By',
      data: {
        pageIndex,
        pageSize,
      }
    }).then(res => {
      if(res.state === 1) {
        setRoleList(res.data.roles);
        setTotal(res.data.totalCount);
      } else {
        messageApi.error(res.data.errorMessage || res.errorMessage);
      }
    }).finally(() => {
      setLoading(false);
    })
  }

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [name, setName] = useState('');
  const [sort, setSort] = useState<number|null>(0);
  const [isActive, setIsActive] = useState(1);
  const [remark, setRemark] = useState('');

  const handleCreateOk = () => {
    Https.post({
      url: 'Api/URPM/V1/Role/Create',
      data: {
        name,
        sort,
        isActive,
        remark
      }
    }).then(res => {
      if(res.state === 1) {
        messageApi.success('添加成功');
        setIsCreateModalOpen(false);
        getRoleList();
      } else {
        messageApi.error(res.data.errorMessage || res.errorMessage);
      }
    })
  }

  const handleCreateCancel = () => {
    setIsCreateModalOpen(false);
    resetCreateInfo();
  }

  const resetCreateInfo = () => {
    setName('');
    setSort(0);
    setIsActive(1);
    setRemark('');
  }

  const createRole = () => {
    resetCreateInfo();
    setIsCreateModalOpen(true);
  }

  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editId, setEditId] = useState('');
  const [editName, setEditName] = useState('');
  const [editSort, setEditSort] = useState<number|null>(0);
  const [editIsActive, setEditIsActive] = useState(1);
  const [editRemark, setEditRemark] = useState('');

  const resetEditInfo = () => {
    setEditName('');
    setEditSort(0);
    setEditIsActive(1);
    setEditRemark('');
  }

  const handleRoleEdit = (record: any) => {
    setEditId(record.id);
    setEditName(record.name);
    setEditIsActive(record.isActive);
    setEditRemark(record.remark);
    setEditSort(record.sort);
    setIsEditModalOpen(true);
  }

  const handleEditOk = () => {
    Https.post({
      url: 'Api/URPM/V1/Role/Update',
      data: {
        id: editId,
        name: editName,
        sort: editSort,
        isActive: editIsActive,
        remark: editRemark
      }
    }).then(res => {
      if(res.state === 1) {
        messageApi.success('编辑成功');
        setIsEditModalOpen(false);
        getRoleList();
      } else {
        messageApi.error(res.data.errorMessage || res.errorMessage);
      }
    })
  }

  const handleEditCancel = () => {
    resetEditInfo();
    setIsEditModalOpen(false);
  }

  const handleSetPermission = (record: any) => {
    navigate(`/main/roleList/setPermission`, { state: { roleId: record.id, roleName: record.name } });
    dispatch({
      type: 'PUSH_MENU_KEYPATH',
      payload: {
        selectedKeys: 'setPermission',
        keyPath: '设置权限',
      }
    })
  }

  useEffect(() => {
    getRoleList();
  }, [])

  useEffect(() => {
    getRoleList();
  }, [pageIndex, pageSize])

  return (
    <section className="Role">
      <section className="create-role">
        <Button type="primary" icon={<FolderAddOutlined />} onClick={() => createRole()}>添加角色</Button>
      </section>
      <section>
        <Table
          dataSource={roleList}
          rowKey="id"
          columns={roleColumns}
          pagination={false}
          scroll={{x: 'max-content', y: 'calc(100vh - 320px)'}}
          loading={loading}
         />
      </section>
      <section className="page-box">
        <Pagination
          align="end"
          total={total}
          showTotal={(total) => `共 ${total} 条`}
          defaultPageSize={20}
          defaultCurrent={1}
          onChange={(page, pageSize) => {
            setPageIndex(page);
            setPageSize(pageSize);
          }}
        />
      </section>
      <Modal
        title="添加角色"
        closable={{ 'aria-label': 'Custom Close Button' }}
        open={isCreateModalOpen}
        onOk={handleCreateOk}
        onCancel={handleCreateCancel}
        destroyOnHidden={true}
        okButtonProps={{
          disabled: !name
        }}>
        <section className="modal-form-item">
          <span className="label required">角色名称</span>
          <Input placeholder="请输入场景名称" value={name} allowClear onChange={(e) => setName(e.target.value)} />
        </section>
         <section className="modal-form-item">
          <span className="label">排序</span>
          <InputNumber min={0} value={sort} onChange={(value) => setSort(value)} />
        </section>
        <section className="modal-form-item">
          <span className="label">是否有效</span>
          <Radio.Group
            value={isActive}
            options={[
              { value: 1, label: '是' },
              { value: 0, label: '否' },
            ]}
            onChange={(e) => setIsActive(e.target.value)}
          />
        </section>
        <section className="modal-form-item">
          <span className="label">备注</span>
          <TextArea rows={2} value={remark} placeholder="请输入备注" onChange={(e) => setRemark(e.target.value)} />
        </section>
      </Modal>
      <Modal
        title="编辑角色"
        closable={{ 'aria-label': 'Custom Close Button' }}
        open={isEditModalOpen}
        onOk={handleEditOk}
        onCancel={handleEditCancel}
        destroyOnHidden={true}
        okButtonProps={{
          disabled: !editName
        }}>
        <section className="modal-form-item">
          <span className="label required">角色名称</span>
          <Input placeholder="请输入场景名称" value={editName} allowClear onChange={(e) => setEditName(e.target.value)} />
        </section>
        <section className="modal-form-item">
          <span className="label">排序</span>
          <InputNumber min={0} value={editSort} onChange={(value) => setEditSort(value)} />
        </section>
        <section className="modal-form-item">
          <span className="label">是否有效</span>
          <Radio.Group
            value={editIsActive}
            options={[
              { value: 1, label: '是' },
              { value: 0, label: '否' },
            ]}
            onChange={(e) => setEditIsActive(e.target.value)}
          />
        </section>
        <section className="modal-form-item">
          <span className="label">备注</span>
          <TextArea rows={2} value={editRemark} placeholder="请输入备注" onChange={(e) => setEditRemark(e.target.value)} />
        </section>
      </Modal>
      { messageContextHolder }
    </section>
  )
}